type SearchableUser = {
  id: string;
  sourceCrm?: string | null;
  externalId?: string | null;
  email?: string | null;
  name?: string | null;
  userIntent?: string[];
};

/**
 * Dedupe/exclusion key for a user. Keyed on the CRM record, never on email or
 * name — colleagues routinely share a branch mailbox (and occasionally a name),
 * and keying on those collapsed them into one selectable option.
 */
export function userIdentityKey(user: SearchableUser): string {
  if (user.sourceCrm && user.externalId) {
    return `${user.sourceCrm}:${user.externalId}`.trim().toLowerCase();
  }
  return user.id;
}

export function userDisplayLabel(user: SearchableUser): string {
  const baseLabel = user.name || user.email || user.id;
  if (user.userIntent && user.userIntent.length > 0) {
    return `${baseLabel} [${user.userIntent.join(", ")}]`;
  }
  return baseLabel;
}

function matchesWordPrefixSearch(label: string, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const words = label.toLowerCase().split(/\s+/);
  // Every query token must prefix-match some word in the label, so a
  // multi-word query like "Catherine Davies" matches "Catherine Davies".
  return q.split(/\s+/).every((token) => words.some((word) => word.startsWith(token)));
}

export function buildUserSelectOptions(
  users: SearchableUser[],
  options: {
    searchQuery: string;
    excludeUserIds?: Iterable<string>;
    excludeIdentityKeys?: Iterable<string>;
  },
): { value: string; label: string }[] {
  const excludeIds = new Set(options.excludeUserIds);
  const excludeKeys = new Set(options.excludeIdentityKeys);
  const seen = new Set<string>();

  return users.reduce<{ value: string; label: string }[]>((acc, user) => {
    const key = userIdentityKey(user);
    if (excludeIds.has(user.id) || excludeKeys.has(key) || seen.has(key)) return acc;
    const label = userDisplayLabel(user);
    // Email is matched too, not just the label: the server search accepts an
    // email fragment, and results it returns would otherwise be filtered back
    // out here because the visible label never contains the address.
    const haystack = user.email ? `${label} ${user.email}` : label;
    if (!matchesWordPrefixSearch(haystack, options.searchQuery)) return acc;
    seen.add(key);
    acc.push({ value: user.id, label });
    return acc;
  }, []);
}
